import { useState } from 'react'
import { useModuleEnabled } from '../navigation'
import { useCreateRequest } from '../lib/createRequest'
import {
  useDeleteIntranetGroup,
  useDeleteIntranetTask,
  useIntranetGroups,
  useIntranetTasks,
  useSaveIntranetGroup,
  useSaveIntranetTask,
  useSetIntranetTaskDone,
  useUsers
} from '../api/queries'
import type { IntranetGroup, IntranetTask } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { formatDate } from '../lib/format'
import { ByLine } from './IntranetBits'
import { Avatar, Card, EmptyState } from './ui'

/**
 * Arbejdsgrupper (docs/DECISIONS.md beslutning 101) og interne opgaver
 * (beslutning 102).
 *
 * <b>Grupperne er husets egne hold</b> - ikke Teams i Microsoft 365. De bruges til
 * at invitere mange på én gang, til opslag der kun angår holdet, og til opgaver,
 * som holdet selv tager.
 */

/** Ét hold med medlemmer, formand og åbne opgaver. */
export function GroupCard({ group, onEdit }: { group: IntranetGroup; onEdit?: (group: IntranetGroup) => void }) {
  const [showTasks, setShowTasks] = useState(false)

  return (
    <Card
      title={`${group.icon ? `${group.icon} ` : '👥 '}${group.name}${group.isActive ? '' : ' (slukket)'}`}
      actions={
        <div className="row" style={{ gap: 6 }}>
          {group.openTaskCount > 0 && (
            <button className="btn btn--sm" onClick={() => setShowTasks((v) => !v)}>
              {group.openTaskCount} åbne opgaver
            </button>
          )}
          {onEdit && (
            <button className="btn btn--sm" onClick={() => onEdit(group)}>
              Ret
            </button>
          )}
        </div>
      }
    >
      {group.description && (
        <p className="small" style={{ marginTop: 0 }}>
          {group.description}
        </p>
      )}

      <div className="tiny muted">
        {group.leader ? `Formand: ${group.leader.displayName}` : 'Ingen formand'} · {group.memberCount} medlemmer
        {group.isMember && ' · du er med'}
      </div>

      <div className="row" style={{ gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        {group.members.map((member) => (
          <span key={member.id} className="row" style={{ gap: 6, alignItems: 'center' }}>
            <Avatar user={member} size={26} />
            <span className="tiny">{member.displayName}</span>
          </span>
        ))}
      </div>

      {showTasks && (
        <div style={{ marginTop: 12 }}>
          <TaskList groupId={group.id} />
        </div>
      )}
    </Card>
  )
}

/** Opret eller ret en arbejdsgruppe. Kun kontoret. */
export function GroupForm({ group, onClose }: { group: IntranetGroup | null; onClose: () => void }) {
  const save = useSaveIntranetGroup()
  const remove = useDeleteIntranetGroup()
  const { data: users } = useUsers()

  const [name, setName] = useState(group?.name ?? '')
  const [description, setDescription] = useState(group?.description ?? '')
  const [icon, setIcon] = useState(group?.icon ?? '')
  const [leaderId, setLeaderId] = useState(group?.leader ? String(group.leader.id) : '')
  const [isActive, setIsActive] = useState(group?.isActive ?? true)
  // Tager imod naerved-haendelser (beslutning 113).
  const [receivesIncidents, setReceivesIncidents] = useState(group?.receivesIncidents ?? false)
  // Pakker varer til bilerne (beslutning 127) - kun naar funktionen er slaaet til.
  const [packsVanOrders, setPacksVanOrders] = useState(group?.packsVanOrders ?? false)
  const vanOrdersOn = useModuleEnabled()('stock_van_orders')
  const [memberIds, setMemberIds] = useState<number[]>(group?.members.map((m) => m.id) ?? [])
  const [error, setError] = useState<string | null>(null)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)

    try {
      await save.mutateAsync({
        id: group?.id,
        input: {
          name: name.trim(),
          description: description.trim() || null,
          icon: icon.trim() || null,
          leaderUserId: leaderId ? Number(leaderId) : null,
          isActive,
          receivesIncidents,
          packsVanOrders,
          memberIds
        }
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Arbejdsgruppen kunne ikke gemmes.')
    }
  }

  return (
    <Card title={group ? 'Ret arbejdsgruppen' : 'Ny arbejdsgruppe'}>
      <form className="stack" onSubmit={submit}>
        <div className="form-grid">
          <label className="field">
            <span className="field__label">Navn *</span>
            <input required maxLength={120} value={name} onChange={(e) => setName(e.target.value)} />
          </label>

          <label className="field">
            <span className="field__label">Ikon</span>
            <input maxLength={8} placeholder="F.eks. 🔧" value={icon} onChange={(e) => setIcon(e.target.value)} />
          </label>

          <label className="field">
            <span className="field__label">Formand</span>
            <select value={leaderId} onChange={(e) => setLeaderId(e.target.value)}>
              <option value="">Ingen formand</option>
              {(users ?? []).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.displayName}
                </option>
              ))}
            </select>
            <span className="tiny muted">Formanden er altid med i gruppen.</span>
          </label>
        </div>

        <label className="field">
          <span className="field__label">Hvad laver gruppen?</span>
          <input maxLength={500} value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>

        <div className="field">
          <span className="field__label">
            Medlemmer {memberIds.length > 0 && <span className="tiny muted">({memberIds.length} valgt)</span>}
          </span>
          <div className="cal-people">
            {(users ?? []).map((u) => (
              <label key={u.id} className={`cal-person ${memberIds.includes(u.id) ? 'cal-person--on' : ''}`}>
                <input
                  type="checkbox"
                  checked={memberIds.includes(u.id)}
                  onChange={() =>
                    setMemberIds((current) =>
                      current.includes(u.id) ? current.filter((x) => x !== u.id) : [...current, u.id]
                    )
                  }
                />
                <span>{u.displayName}</span>
              </label>
            ))}
          </div>
        </div>

        <label className="row small" style={{ gap: 8 }}>
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
          Kan vælges
        </label>

        <label className="row small" style={{ gap: 8, alignItems: 'flex-start' }}>
          <input
            type="checkbox"
            style={{ width: 'auto', marginTop: 3 }}
            checked={receivesIncidents}
            onChange={(e) => setReceivesIncidents(e.target.checked)}
          />
          <span>
            Tager imod nærved-hændelser
            <span className="tiny muted" style={{ display: 'block' }}>
              Medlemmerne får besked, når nogen melder en hændelse under Arbejdsmiljø, og følger op på den.
            </span>
          </span>
        </label>

        {vanOrdersOn && (
          <label className="row small" style={{ gap: 8, alignItems: 'flex-start' }}>
            <input
              type="checkbox"
              style={{ width: 'auto', marginTop: 3 }}
              checked={packsVanOrders}
              onChange={(e) => setPacksVanOrders(e.target.checked)}
            />
            <span>
              Pakker varer til bilerne
              <span className="tiny muted" style={{ display: 'block' }}>
                Medlemmerne får besked, når en montør bestiller varer til sin bil, og pakker dem. Har ingen gruppe
                fluebenet, er det kontoret.
              </span>
            </span>
          </label>
        )}

        {error && <div className="error-box">{error}</div>}

        <div className="btn-group">
          <button className="btn btn--primary" type="submit" disabled={save.isPending}>
            {save.isPending ? 'Gemmer ...' : 'Gem'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Fortryd
          </button>
          {group && (
            <button
              className="btn"
              type="button"
              onClick={async () => {
                if (!window.confirm(`Slet arbejdsgruppen "${group.name}"? Opslag og opgaver bliver stående.`)) return
                await remove.mutateAsync(group.id)
                onClose()
              }}
            >
              Slet
            </button>
          )}
        </div>
      </form>
    </Card>
  )
}

/** Fanen med arbejdsgrupperne. */
export function GroupsTab() {
  const { isIntranetAdmin: isAdmin } = useAuth()
  const { data } = useIntranetGroups(isAdmin)
  const [editing, setEditing] = useState<IntranetGroup | null | undefined>(undefined)

  return (
    <div className="stack" style={{ gap: 16 }}>
      {isAdmin && editing === undefined && (
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button className="btn btn--primary" onClick={() => setEditing(null)}>
            Ny arbejdsgruppe
          </button>
        </div>
      )}

      {editing !== undefined && <GroupForm group={editing} onClose={() => setEditing(undefined)} />}

      {data?.length === 0 && (
        <EmptyState>
          Ingen arbejdsgrupper endnu.
          {isAdmin ? ' Tryk "Ny arbejdsgruppe" og saml et hold.' : ''}
        </EmptyState>
      )}

      {data?.map((group) => (
        <GroupCard key={group.id} group={group} onEdit={isAdmin ? (g) => setEditing(g) : undefined} />
      ))}
    </div>
  )
}

/** Fanen med de interne opgaver. */
export function TasksTab() {
  const [mine, setMine] = useState(false)
  const [includeDone, setIncludeDone] = useState(false)
  const [adding, setAdding] = useState(false)
  useCreateRequest(['opgave'], () => setAdding(true))

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="row row--between" style={{ gap: 8, flexWrap: 'wrap' }}>
        <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
          <label className="row small muted" style={{ gap: 6 }}>
            <input
              type="checkbox"
              style={{ width: 'auto' }}
              checked={mine}
              onChange={(e) => setMine(e.target.checked)}
            />
            Kun mine
          </label>
          <label className="row small muted" style={{ gap: 6 }}>
            <input
              type="checkbox"
              style={{ width: 'auto' }}
              checked={includeDone}
              onChange={(e) => setIncludeDone(e.target.checked)}
            />
            Vis også færdige
          </label>
        </div>

        {!adding && (
          <button className="btn btn--primary" onClick={() => setAdding(true)}>
            Ny opgave
          </button>
        )}
      </div>

      {adding && <TaskForm task={null} onClose={() => setAdding(false)} />}

      <TaskList mine={mine} includeDone={includeDone} />
    </div>
  )
}

/** Listen over interne opgaver. Bruges både på fanen og inde i en gruppe. */
export function TaskList({ mine, includeDone, groupId }: { mine?: boolean; includeDone?: boolean; groupId?: number }) {
  const { data } = useIntranetTasks({ mine, includeDone, groupId })
  const [editing, setEditing] = useState<IntranetTask | null>(null)

  if (!data) return null

  if (data.length === 0) {
    return <EmptyState>Ingen opgaver her. Tryk "Ny opgave", når der er noget, der skal laves.</EmptyState>
  }

  return (
    <div className="stack" style={{ gap: 8 }}>
      {editing && <TaskForm task={editing} onClose={() => setEditing(null)} />}

      {data.map((task) => (
        <TaskRow key={task.id} task={task} onEdit={() => setEditing(task)} />
      ))}
    </div>
  )
}

/** Én intern opgave med flueben. */
function TaskRow({ task, onEdit }: { task: IntranetTask; onEdit: () => void }) {
  const setDone = useSetIntranetTaskDone()
  const remove = useDeleteIntranetTask()
  const { user, isIntranetAdmin: isAdmin } = useAuth()
  const [error, setError] = useState<string | null>(null)

  const overdue = !task.isDone && task.dueDate ? new Date(task.dueDate) < new Date() : false
  const canDelete = isAdmin || task.createdBy?.id === user?.id

  async function run(action: () => Promise<unknown>) {
    setError(null)
    try {
      await action()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Handlingen mislykkedes.')
    }
  }

  return (
    <div className="card card--flat" style={{ padding: 12, opacity: task.isDone ? 0.6 : 1 }}>
      <div className="row row--between" style={{ gap: 10, alignItems: 'flex-start' }}>
        <label className="row" style={{ gap: 10, minWidth: 0, alignItems: 'flex-start' }}>
          <input
            type="checkbox"
            style={{ width: 'auto', marginTop: 3 }}
            disabled={!task.canClose || setDone.isPending}
            checked={task.isDone}
            onChange={(e) => void run(() => setDone.mutateAsync({ id: task.id, done: e.target.checked }))}
          />
          <span style={{ minWidth: 0 }}>
            <span className="small" style={{ textDecoration: task.isDone ? 'line-through' : undefined }}>
              {task.isPrivate && <span title="Privat - kun du og den anden fra samtalen ser den">🔒 </span>}
              {task.title}
            </span>
            {task.description && <div className="tiny muted">{task.description}</div>}
            <div className="tiny muted">
              {task.assignee
                ? task.assignee.displayName
                : task.groupName
                  ? `👥 ${task.groupName}`
                  : 'Ingen har taget den'}
              {task.dueDate && (
                <span className={overdue ? 'warn' : undefined}> · frist {formatDate(task.dueDate)}</span>
              )}
              {task.isDone && task.doneBy && <> · lukket af {task.doneBy.displayName}</>}
            </div>
          </span>
        </label>

        <div className="row" style={{ gap: 6, flexShrink: 0 }}>
          {task.canClose && (
            <button className="btn btn--sm" onClick={onEdit}>
              Ret
            </button>
          )}
          {canDelete && (
            <button
              className="btn btn--sm"
              disabled={remove.isPending}
              onClick={() => {
                if (window.confirm(`Slet opgaven "${task.title}"?`)) void run(() => remove.mutateAsync(task.id))
              }}
            >
              Slet
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="error-box" style={{ marginTop: 6 }}>
          {error}
        </div>
      )}
    </div>
  )
}

/** Skriv eller ret en intern opgave. Alle må skrive. */
function TaskForm({ task, onClose }: { task: IntranetTask | null; onClose: () => void }) {
  const save = useSaveIntranetTask()
  const { data: users } = useUsers()
  const { data: groups } = useIntranetGroups()

  const [title, setTitle] = useState(task?.title ?? '')
  const [description, setDescription] = useState(task?.description ?? '')
  const [owner, setOwner] = useState(
    task?.assignee ? `user:${task.assignee.id}` : task?.groupId ? `group:${task.groupId}` : ''
  )
  const [dueDate, setDueDate] = useState(task?.dueDate?.slice(0, 10) ?? '')
  const [error, setError] = useState<string | null>(null)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)

    // Enten en person eller en gruppe - serveren afviser begge dele paa én gang.
    const [kind, id] = owner.split(':')

    try {
      await save.mutateAsync({
        id: task?.id,
        input: {
          title: title.trim(),
          description: description.trim() || null,
          assigneeUserId: kind === 'user' ? Number(id) : null,
          groupId: kind === 'group' ? Number(id) : null,
          dueDate: dueDate ? new Date(`${dueDate}T12:00`).toISOString() : null
        }
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Opgaven kunne ikke gemmes.')
    }
  }

  return (
    <Card title={task ? 'Ret opgaven' : 'Ny opgave'}>
      <form className="stack" onSubmit={submit}>
        <label className="field">
          <span className="field__label">Hvad skal der laves? *</span>
          <input required maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>

        <div className="form-grid">
          <label className="field">
            <span className="field__label">Hvem tager den?</span>
            <select value={owner} onChange={(e) => setOwner(e.target.value)}>
              <option value="">Ingen - den ligger frit</option>
              <optgroup label="Personer">
                {(users ?? []).map((u) => (
                  <option key={u.id} value={`user:${u.id}`}>
                    {u.displayName}
                  </option>
                ))}
              </optgroup>
              {groups && groups.length > 0 && (
                <optgroup label="Arbejdsgrupper">
                  {groups.map((g) => (
                    <option key={g.id} value={`group:${g.id}`}>
                      {g.icon ? `${g.icon} ` : ''}
                      {g.name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </label>

          <label className="field">
            <span className="field__label">Frist</span>
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </label>
        </div>

        <label className="field">
          <span className="field__label">Uddybning</span>
          <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>

        {error && <div className="error-box">{error}</div>}

        <div className="btn-group">
          <button className="btn btn--primary" type="submit" disabled={save.isPending}>
            {save.isPending ? 'Gemmer ...' : 'Gem'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Fortryd
          </button>
        </div>
      </form>
    </Card>
  )
}

/** Gør ByLine tilgængelig for gruppesiden uden endnu en import-sti. */
export { ByLine }
